import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Flex,
  Button,
  Icon,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
} from '@chakra-ui/react'
import { PageTransition } from '../../../components/PageTransition'
import { HeaderSection } from '../HeaderSection'
import { usePedidos } from './hooks/usePedidos'
import { FilterForm } from './components/FilterForm'
import { ResultsSummary } from './components/ResultsSummary'
import { PedidosTable } from './components/PedidosTable'
import { StatusLegend } from './components/StatusLegend'
import { FormProvider, useForm } from 'react-hook-form'
import { Link, useSearchParams } from 'react-router-dom'
import { FiArrowLeft, FiRefreshCw } from 'react-icons/fi'
import { api } from '../../../services/api'

export function PedidosPagamentos() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [filtrosAtivos, setFiltrosAtivos] = useState({})
  const [pedidosState, setPedidosState] = useState([])
  const toast = useToast()

  const defaultValues = {
    numeroPedido: '',
    dataInicio: '',
    dataFim: '',
    statusPagamento: '',
  }

  const methods = useForm({
    defaultValues: defaultValues,
  })

  useEffect(() => {
    const filtrosFromUrl = {
      numeroPedido: searchParams.get('numeroPedido') || '',
      dataInicio: searchParams.get('dataInicio') || '',
      dataFim: searchParams.get('dataFim') || '',
      statusPagamento: searchParams.get('statusPagamento') || '',
    }

    methods.reset(filtrosFromUrl)

    if (searchParams.toString()) {
      setFiltrosAtivos(filtrosFromUrl)
    }
  }, [])

  const { data: pedidos = [], isLoading, error, refetch, isFetching, isRefetching } = usePedidos(filtrosAtivos)

  useEffect(() => {
    if (pedidos && JSON.stringify(pedidos) !== JSON.stringify(pedidosState)) {
      setPedidosState(pedidos)
    }
  }, [pedidos])

  const handleFilter = (data) => {
    console.log('Filtros aplicados:', data)

    setFiltrosAtivos(data)

    // Salvar filtros na URL
    const urlParams = new URLSearchParams()

    // Adicionar filtros básicos
    if (data.numeroPedido) urlParams.set('numeroPedido', data.numeroPedido)
    if (data.dataInicio) urlParams.set('dataInicio', data.dataInicio)
    if (data.dataFim) urlParams.set('dataFim', data.dataFim)
    if (data.statusPagamento) urlParams.set('statusPagamento', data.statusPagamento)

    // Atualizar URL sem recarregar a página
    setSearchParams(urlParams)

    toast({
      title: 'Filtros aplicados',
      description: 'Buscando pedidos com os filtros selecionados',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }

  // Função para limpar todos os filtros
  const handleClearFilters = () => {
    // Limpar formulário
    methods.reset(defaultValues, {
      keepDirty: false,
      keepErrors: false,
      keepIsSubmitted: false,
      keepTouched: false,
      keepIsValid: false,
      keepSubmitCount: false,
    })

    // Limpar filtros ativos
    setFiltrosAtivos({})

    // Limpar URL
    setSearchParams({})

    toast({
      title: 'Filtros limpos',
      description: 'Todos os filtros foram removidos',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })

    // Forçar uma atualização dos dados
    setTimeout(() => {
      refetch()
    }, 0)
  }

  const handleRefresh = () => {
    refetch()

    toast({
      title: 'Atualizando dados',
      description: 'Buscando os dados mais recentes',
      status: 'info',
      duration: 2000,
      isClosable: true,
    })
  }

  // Calcular total de peças
  const totalPecas = pedidosState.reduce((sum, p) => {
    const quantidadeStr = typeof p.quantidade === 'string' ? p.quantidade.replace(/\./g, '').replace(/\,/g, '.') : String(p.quantidade)
    return sum + (parseFloat(quantidadeStr) || 0)
  }, 0)

  const handleStatusPagamentoChange = async (pedidoId, novoPagamento) => {
    try {
      // Chamar a API para atualizar o status de pagamento
      await api.put('/site/v2/tercerizado/atualiza-pagamento-tercerizado', {
        id_pedido: pedidoId,
      })

      toast({
        title: 'Status de pagamento atualizado',
        description: novoPagamento ? 'Pedido marcado como pago' : 'Pedido marcado como não pago',
        status: 'success',
        duration: 3000,
        isClosable: true,
      })

      // Recarregar dados após a atualização
      refetch()
    } catch (error) {
      console.error('Erro ao atualizar status de pagamento:', error)
      toast({
        title: 'Erro ao atualizar status',
        description: 'Não foi possível atualizar o status de pagamento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      })
    }
  }

  return (
    <PageTransition>
      <Box bg="white" minHeight="100vh">
        <HeaderSection title="STATUS DE PAGAMENTOS" />

        <Container maxW="1366px" px={2} py={6} overflow="visible">
          <FormProvider {...methods}>
            {/* Componente de filtros */}
            <FilterForm onSubmit={handleFilter} onClear={handleClearFilters} isSubmitting={isLoading || isFetching} />

            {/* Componente de resumo de resultados com botão de atualização e legenda */}
            <Flex justifyContent="space-between" alignItems="center" mb={4}>
              <ResultsSummary totalPedidos={pedidosState.length} totalItens={pedidosState.length} totalPecas={totalPecas} />

              <Flex alignItems="center">
                <StatusLegend />
                <Button
                  size="sm"
                  leftIcon={<Icon as={FiRefreshCw} />}
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleRefresh}
                  isLoading={isRefetching}
                  loadingText="Atualizando"
                  disabled={isLoading || isFetching}
                  fontSize="12px"
                >
                  Atualizar
                </Button>
              </Flex>
            </Flex>

            {/* Componente da tabela de pedidos */}
            <PedidosTable
              pedidos={pedidosState}
              isLoading={isLoading}
              isFetching={isFetching}
              isRefetching={isRefetching}
              error={error}
              onStatusPagamentoChange={handleStatusPagamentoChange}
            />

            {/* Botão voltar */}
            <Flex mt={8} mb={10} justifyContent="center">
              <Button
                leftIcon={<Icon as={FiArrowLeft} />}
                variant="ghost"
                as={Link}
                to="/omnipromo"
                color="#313E44"
                fontWeight="normal"
                fontSize="12px"
              >
                Voltar
              </Button>
            </Flex>
          </FormProvider>
        </Container>
      </Box>
    </PageTransition>
  )
}
